import React, { useState, useEffect, useCallback } from 'react';
import { 
  Wifi, User, Mail, Phone, MapPin, Package, Clock, CheckCircle2, 
  Circle, ArrowRight, LogOut, AlertCircle, Loader2, FileText, 
  PenTool, Calendar, Shield, ChevronRight, CreditCard, Building2, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============== API HELPERS ==============
const portalApi = {
  async login(email, password) {
    const res = await fetch(`${API_URL}/api/portal/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.detail || 'Login failed');
    }
    return data;
  },

  async setupPassword(resetToken, newPassword) {
    const res = await fetch(`${API_URL}/api/portal/setup-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reset_token: resetToken, new_password: newPassword })
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Failed to set password');
    }
    return res.json();
  },

  async getDashboard(token) {
    const res = await fetch(`${API_URL}/api/portal/dashboard?token=${token}`);
    if (!res.ok) throw new Error('Failed to load dashboard');
    return res.json();
  },

  async getAgreement(orderId, token) {
    const res = await fetch(`${API_URL}/api/portal/agreement/${orderId}?token=${token}`);
    if (!res.ok) throw new Error('Failed to load agreement');
    return res.json();
  },

  async signAgreement(orderId, token, signatureData, directDebit) {
    const res = await fetch(`${API_URL}/api/portal/sign-agreement`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        token: token, 
        order_id: orderId, 
        signature_data: signatureData,
        account_holder_name: directDebit.accountHolderName,
        iban: directDebit.iban,
        bic: directDebit.bic,
        bank_name: directDebit.bankName
      })
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Failed to sign agreement');
    }
    return res.json();
  }
};

// ============== ORDER FLOW PROGRESS ==============
const OrderProgress = ({ flowProgress, steps }) => {
  const getStepStatus = (step) => {
    if (flowProgress[step.key]) return 'completed';
    const completedSteps = steps.filter(s => flowProgress[s.key]);
    const currentIndex = completedSteps.length;
    const stepIndex = steps.findIndex(s => s.key === step.key);
    if (stepIndex === currentIndex) return 'current';
    return 'pending';
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="font-semibold text-slate-900 mb-6">Order Progress</h3>
      <div className="space-y-4">
        {steps.map((step, index) => {
          const status = getStepStatus(step);
          return (
            <div key={step.key} className="flex items-start gap-4">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  status === 'completed' ? 'bg-green-500 text-white' :
                  status === 'current' ? 'bg-[#1E3064] text-white' :
                  'bg-slate-200 text-slate-400'
                }`}>
                  {status === 'completed' ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-semibold">{step.step}</span>
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-0.5 h-8 mt-2 ${
                    status === 'completed' ? 'bg-green-500' : 'bg-slate-200'
                  }`} />
                )}
              </div>
              <div className="flex-1 pt-1">
                <p className={`font-medium ${
                  status === 'completed' ? 'text-green-600' :
                  status === 'current' ? 'text-[#1E3064]' :
                  'text-slate-400'
                }`}>
                  {step.name}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Helper function to replace merge fields in agreement content
const replaceMergeFields = (content, data) => {
  if (!content) return '';
  return content
    .replace(/\{\{customer_name\}\}/g, data.customer_name || '')
    .replace(/\{\{customer_email\}\}/g, data.customer_email || '')
    .replace(/\{\{customer_phone\}\}/g, data.customer_phone || '')
    .replace(/\{\{address_line1\}\}/g, data.installation_address?.address_line1 || '')
    .replace(/\{\{address_line2\}\}/g, data.installation_address?.address_line2 || '')
    .replace(/\{\{city\}\}/g, data.installation_address?.city || '')
    .replace(/\{\{county\}\}/g, data.installation_address?.county || '')
    .replace(/\{\{eircode\}\}/g, data.installation_address?.eircode || '')
    .replace(/\{\{order_number\}\}/g, data.order_number || '')
    .replace(/\{\{order_date\}\}/g, data.order_date ? new Date(data.order_date).toLocaleDateString('en-IE') : '')
    .replace(/\{\{service_type\}\}/g, data.service_type || 'Home')
    .replace(/\{\{package_name\}\}/g, data.package_name || '')
    .replace(/\{\{package_price\}\}/g, data.package_price?.toFixed(2) || '0.00')
    .replace(/\{\{download_speed\}\}/g, data.download_speed || '')
    .replace(/\{\{upload_speed\}\}/g, data.upload_speed || '')
    .replace(/\{\{addons\}\}/g, data.addons || '')
    .replace(/\{\{total_monthly\}\}/g, data.total_monthly?.toFixed(2) || '0.00')
    .replace(/\{\{total_one_time\}\}/g, data.total_one_time?.toFixed(2) || '0.00')
    // Handle conditional sections
    .replace(/\{\{#addons\}\}[\s\S]*?\{\{\/addons\}\}/g, data.addons ? data.addons : '')
    .replace(/\{\{#total_one_time\}\}[\s\S]*?\{\{\/total_one_time\}\}/g, data.total_one_time > 0 ? `One-time Fees: €${data.total_one_time?.toFixed(2)}` : '');
};

// Helper to mask IBAN - show first 8 and last 4 only
const maskIBAN = (iban) => {
  if (!iban) return '';
  const clean = iban.replace(/\s/g, '').toUpperCase();
  if (clean.length <= 12) return clean;
  return clean.substring(0, 8) + '****' + clean.substring(clean.length - 4);
};

// Generate unique signature reference
const generateSignatureRef = (orderId, timestamp) => {
  const date = new Date(timestamp);
  const dateStr = date.toISOString().slice(0,10).replace(/-/g, '');
  const shortId = orderId.substring(0, 8).toUpperCase();
  return `SIG-${dateStr}-${shortId}`;
};

// IBAN validation helper
const validateIBAN = (iban) => {
  const cleanIBAN = iban.replace(/\s/g, '').toUpperCase();
  // Irish IBAN: IE + 2 check digits + 4 bank code + 6 branch code + 8 account number = 22 chars
  if (cleanIBAN.length < 15 || cleanIBAN.length > 34) return false;
  // Check if starts with country code (2 letters) followed by 2 digits
  if (!/^[A-Z]{2}[0-9]{2}/.test(cleanIBAN)) return false;
  return true;
};

// BIC validation helper
const validateBIC = (bic) => {
  const cleanBIC = bic.replace(/\s/g, '').toUpperCase();
  // BIC: 8 or 11 characters
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(cleanBIC);
};

// Format IBAN with spaces
const formatIBAN = (iban) => {
  const clean = iban.replace(/\s/g, '').toUpperCase();
  return clean.replace(/(.{4})/g, '$1 ').trim();
};

// ============== AGREEMENT SIGNING ==============
const AgreementSigning = ({ order, token, onSigned }) => {
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1); // 1: Agreement, 2: Direct Debit, 3: Signature
  const [showAgreementContent, setShowAgreementContent] = useState(true);
  
  // Form state
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [directDebit, setDirectDebit] = useState({
    accountHolderName: `${order.first_name} ${order.last_name}`,
    iban: '',
    bic: '',
    bankName: ''
  });
  const [ibanError, setIbanError] = useState('');
  const [bicError, setBicError] = useState('');

  useEffect(() => {
    const loadAgreement = async () => {
      try {
        const data = await portalApi.getAgreement(order.id, token);
        setAgreement(data);
        // Pre-fill account holder name if available
        if (data.customer_name) {
          setDirectDebit(prev => ({ ...prev, accountHolderName: data.customer_name }));
        }
      } catch (err) {
        setError('Failed to load agreement');
      } finally {
        setLoading(false);
      }
    };
    loadAgreement();
  }, [order.id, token]);

  const handleIBANChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setDirectDebit({ ...directDebit, iban: value });
    if (value && !validateIBAN(value)) {
      setIbanError('Please enter a valid IBAN');
    } else {
      setIbanError('');
    }
  };

  const handleBICChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setDirectDebit({ ...directDebit, bic: value });
    if (value && !validateBIC(value)) {
      setBicError('Please enter a valid BIC/SWIFT code');
    } else {
      setBicError('');
    }
  };

  const isDirectDebitValid = () => {
    return directDebit.accountHolderName.trim() &&
           directDebit.iban.trim() &&
           validateIBAN(directDebit.iban) &&
           directDebit.bic.trim() &&
           validateBIC(directDebit.bic);
  };

  const handleSign = async () => {
    if (!typedName.trim() || !agreed || !isDirectDebitValid()) return;
    
    setSigning(true);
    setError('');
    try {
      await portalApi.signAgreement(order.id, token, typedName.trim(), directDebit);
      onSigned();
    } catch (err) {
      setError(err.message);
    } finally {
      setSigning(false);
    }
  };

  const fullCustomerName = agreement?.customer_name || `${order.first_name} ${order.last_name}`;
  const isNameValid = typedName.trim().toLowerCase() === fullCustomerName.toLowerCase();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3064]" />
      </div>
    );
  }

  if (agreement?.agreement_signed) {
    const signatureRef = generateSignatureRef(order.id, agreement.agreement_signed_at);
    
    return (
      <div className="space-y-6">
        {/* Signed Agreement Header */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-green-900">Agreement Signed</h3>
              <p className="text-sm text-green-700">
                Reference: <span className="font-mono">{signatureRef}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Full Signed Agreement Document */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Document Header */}
          <div className="bg-slate-50 p-6 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/images/geekbroadband-logo.webp" 
                alt="GeekBroadband" 
                className="h-10"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div>
                <h2 className="font-bold text-slate-900">Broadband Service Agreement</h2>
                <p className="text-sm text-slate-500">Order: {agreement.order_number}</p>
              </div>
            </div>
            <div className="text-right text-sm text-slate-500">
              <p>Version {agreement.agreement_version || '1.0'}</p>
              <p className="text-green-600 font-medium">✓ Signed</p>
            </div>
          </div>

          {/* Agreement Content */}
          <div 
            className="prose prose-slate prose-sm max-w-none p-6"
            dangerouslySetInnerHTML={{ 
              __html: replaceMergeFields(agreement?.agreement_content, agreement) 
            }}
          />

          {/* Direct Debit Section */}
          {agreement.direct_debit_terms && (
            <div className="border-t border-slate-200">
              <div 
                className="prose prose-slate prose-sm max-w-none p-6 bg-blue-50"
                dangerouslySetInnerHTML={{ __html: agreement.direct_debit_terms }}
              />
            </div>
          )}

          {/* Direct Debit Mandate Details */}
          {agreement.direct_debit_mandate && (
            <div className="border-t border-slate-200 p-6 bg-slate-50">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-slate-600" />
                Direct Debit Mandate Details
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Mandate Reference</p>
                  <p className="font-mono font-medium">{agreement.direct_debit_mandate.mandate_reference}</p>
                </div>
                <div>
                  <p className="text-slate-500">Account Holder</p>
                  <p className="font-medium">{agreement.direct_debit_mandate.account_holder_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">IBAN</p>
                  <p className="font-mono font-medium">{maskIBAN(agreement.direct_debit_mandate.iban)}</p>
                </div>
                <div>
                  <p className="text-slate-500">BIC</p>
                  <p className="font-mono font-medium">{agreement.direct_debit_mandate.bic}</p>
                </div>
              </div>
            </div>
          )}

          {/* Signature Block - Like a real signed document */}
          <div className="border-t-2 border-slate-300 p-6 bg-white">
            <div className="max-w-md mx-auto">
              <h4 className="font-semibold text-slate-900 mb-4 text-center">Electronic Signature</h4>
              
              <div className="border-2 border-slate-200 rounded-lg p-6 bg-slate-50">
                {/* Signature */}
                <div className="text-center mb-4 pb-4 border-b border-dashed border-slate-300">
                  <p 
                    className="text-3xl text-[#1E3064] mb-2"
                    style={{ fontFamily: "'Dancing Script', cursive", fontWeight: 600 }}
                  >
                    {agreement.signature_data}
                  </p>
                  <div className="w-48 h-0.5 bg-slate-400 mx-auto"></div>
                  <p className="text-sm text-slate-600 mt-2">{agreement.customer_name}</p>
                </div>

                {/* Signature Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Signature Reference</p>
                    <p className="font-mono font-medium text-slate-900">{signatureRef}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Date Signed</p>
                    <p className="font-medium text-slate-900">
                      {new Date(agreement.agreement_signed_at).toLocaleDateString('en-IE', { 
                        day: 'numeric', month: 'long', year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Time Signed</p>
                    <p className="font-medium text-slate-900">
                      {new Date(agreement.agreement_signed_at).toLocaleTimeString('en-IE', {
                        hour: '2-digit', minute: '2-digit', second: '2-digit'
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">IP Address</p>
                    <p className="font-mono font-medium text-slate-900">{agreement.signature_ip || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-slate-500 text-center mt-4">
                This document has been electronically signed and is legally binding.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step indicators
  const steps = [
    { num: 1, name: 'Review Agreement', icon: FileText },
    { num: 2, name: 'Direct Debit', icon: CreditCard },
    { num: 3, name: 'Sign & Confirm', icon: PenTool }
  ];

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <React.Fragment key={step.num}>
              <button
                onClick={() => setCurrentStep(step.num)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                  currentStep === step.num
                    ? 'bg-[#1E3064] text-white'
                    : currentStep > step.num
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                <step.icon className="w-4 h-4" />
                <span className="hidden sm:inline text-sm font-medium">{step.name}</span>
              </button>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.num ? 'bg-green-500' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Step 1: Agreement Content */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1E3064]/10 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#1E3064]" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">{agreement?.title || 'Broadband Service Agreement'}</h3>
                  <p className="text-sm text-slate-500">Order: {agreement?.order_number} • Version {agreement?.agreement_version || '1.0'}</p>
                </div>
              </div>
              <button
                onClick={() => setShowAgreementContent(!showAgreementContent)}
                className="text-slate-400 hover:text-slate-600 p-2"
              >
                {showAgreementContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
            </div>

            {showAgreementContent && (
              <div 
                className="prose prose-slate prose-sm max-w-none bg-slate-50 rounded-lg p-6 max-h-[500px] overflow-y-auto"
                dangerouslySetInnerHTML={{ 
                  __html: replaceMergeFields(agreement?.agreement_content, agreement) 
                }}
              />
            )}
          </div>

          <button
            onClick={() => setCurrentStep(2)}
            className="w-full py-4 rounded-lg font-semibold bg-gradient-to-r from-[#1E3064] to-[#2a4080] text-white hover:shadow-lg transition flex items-center justify-center gap-2"
          >
            Continue to Direct Debit
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Step 2: Direct Debit Mandate */}
      {currentStep === 2 && (
        <div className="space-y-6">
          {/* Direct Debit Terms */}
          {agreement?.direct_debit_terms && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">SEPA Direct Debit Mandate</h3>
                  <p className="text-sm text-slate-500">Your rights and protections</p>
                </div>
              </div>
              <div 
                className="prose prose-slate prose-sm max-w-none bg-blue-50 rounded-lg p-4 text-blue-900"
                dangerouslySetInnerHTML={{ __html: agreement.direct_debit_terms }}
              />
            </div>
          )}

          {/* Bank Details Form */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#F97530]/10 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-[#F97530]" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Bank Account Details</h3>
                <p className="text-sm text-slate-500">For monthly Direct Debit payments</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Account Holder Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={directDebit.accountHolderName}
                  onChange={(e) => setDirectDebit({ ...directDebit, accountHolderName: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#1E3064] outline-none"
                  placeholder="Name as it appears on bank account"
                  data-testid="dd-account-holder-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  IBAN <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formatIBAN(directDebit.iban)}
                  onChange={handleIBANChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#1E3064] outline-none font-mono ${
                    ibanError ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="IE29 AIBK 9311 5212 3456 78"
                  data-testid="dd-iban-input"
                />
                {ibanError && <p className="text-sm text-red-500 mt-1">{ibanError}</p>}
                <p className="text-xs text-slate-500 mt-1">Example Irish IBAN: IE29 AIBK 9311 5212 3456 78</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    BIC / SWIFT Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={directDebit.bic}
                    onChange={handleBICChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#1E3064] outline-none font-mono ${
                      bicError ? 'border-red-300' : 'border-slate-300'
                    }`}
                    placeholder="AIBKIE2D"
                    data-testid="dd-bic-input"
                  />
                  {bicError && <p className="text-sm text-red-500 mt-1">{bicError}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Bank Name (optional)
                  </label>
                  <input
                    type="text"
                    value={directDebit.bankName}
                    onChange={(e) => setDirectDebit({ ...directDebit, bankName: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#1E3064] outline-none"
                    placeholder="e.g., AIB, Bank of Ireland"
                    data-testid="dd-bank-name-input"
                  />
                </div>
              </div>

              {/* Common Irish Bank BICs */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Common Irish Bank BIC Codes:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <span className="bg-white px-2 py-1 rounded border">AIB: AIBKIE2D</span>
                  <span className="bg-white px-2 py-1 rounded border">BOI: BOFIIE2D</span>
                  <span className="bg-white px-2 py-1 rounded border">PTSB: IPBSIE2D</span>
                  <span className="bg-white px-2 py-1 rounded border">Ulster: ABORIESD</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => setCurrentStep(1)}
              className="flex-1 py-4 rounded-lg font-semibold border-2 border-slate-300 text-slate-700 hover:bg-slate-50 transition"
            >
              Back
            </button>
            <button
              onClick={() => setCurrentStep(3)}
              disabled={!isDirectDebitValid()}
              className={`flex-1 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition ${
                isDirectDebitValid()
                  ? 'bg-gradient-to-r from-[#1E3064] to-[#2a4080] text-white hover:shadow-lg'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              Continue to Sign
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Signature */}
      {currentStep === 3 && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-4">Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-slate-500 mb-1">Package</p>
                <p className="font-semibold text-slate-900">{agreement?.package_name}</p>
                <p className="text-[#F97530] font-bold">€{agreement?.total_monthly?.toFixed(2)}/month <span className="text-xs text-slate-400 font-normal">excl. VAT</span></p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-slate-500 mb-1">Direct Debit</p>
                <p className="font-semibold text-slate-900">{directDebit.accountHolderName}</p>
                <p className="font-mono text-xs">{formatIBAN(directDebit.iban)}</p>
              </div>
            </div>
          </div>

          {/* E-Signature */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#F97530]/10 rounded-lg flex items-center justify-center">
                <PenTool className="w-5 h-5 text-[#F97530]" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Electronic Signature</h3>
                <p className="text-sm text-slate-500">Type your full legal name to sign both documents</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Type your full name exactly as: <strong>{fullCustomerName}</strong>
                </label>
                <input
                  type="text"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="Type your full name..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#1E3064] outline-none text-lg"
                  data-testid="signature-name-input"
                />
              </div>

              {typedName && (
                <div className="bg-slate-50 rounded-lg p-6 border-2 border-dashed border-slate-300">
                  <p className="text-sm text-slate-500 mb-2">Your Electronic Signature:</p>
                  <p 
                    className="text-3xl text-[#1E3064]"
                    style={{ fontFamily: "'Dancing Script', cursive", fontWeight: 600 }}
                  >
                    {typedName}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    {new Date().toLocaleDateString('en-IE', { 
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                    })}
                  </p>
                </div>
              )}

              <label className="flex items-start gap-3 cursor-pointer bg-amber-50 rounded-lg p-4 border border-amber-200">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-slate-300 text-[#1E3064] focus:ring-[#1E3064]"
                  data-testid="agreement-checkbox"
                />
                <span className="text-sm text-slate-700">
                  <strong>I confirm that:</strong>
                  <ul className="list-disc ml-4 mt-2 text-slate-600">
                    <li>I have read and agree to the Broadband Service Agreement</li>
                    <li>I authorize GeekBroadband to collect payments via Direct Debit</li>
                    <li>The bank account details I provided are correct</li>
                    <li>Typing my name above constitutes my legal electronic signature</li>
                  </ul>
                </span>
              </label>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => setCurrentStep(2)}
              className="flex-1 py-4 rounded-lg font-semibold border-2 border-slate-300 text-slate-700 hover:bg-slate-50 transition"
            >
              Back
            </button>
            <button
              onClick={handleSign}
              disabled={!isNameValid || !agreed || signing}
              className={`flex-1 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition ${
                isNameValid && agreed && !signing
                  ? 'bg-gradient-to-r from-[#F97530] to-[#FF9F5B] text-white hover:shadow-lg'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
              data-testid="sign-agreement-btn"
            >
              {signing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  Sign Agreement & Direct Debit
                </>
              )}
            </button>
          </div>

          {!isNameValid && typedName && (
            <p className="text-sm text-amber-600 text-center">
              Please type your name exactly as shown: "{fullCustomerName}"
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ============== LOGIN SCREEN ==============
const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await portalApi.login(email, password);
      onLogin(result.token, result.customer);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E3064] via-[#2a4080] to-[#1E3064] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          {/* GeekBroadband Logo */}
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/images/geekbroadband-logo.webp" 
              alt="GeekBroadband" 
              className="h-12 object-contain"
              onError={(e) => { 
                e.target.style.display = 'none'; 
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-r from-[#F97530] to-[#FF9F5B] rounded-xl flex items-center justify-center">
                <Wifi className="w-5 h-5 text-white" />
              </div>
              <span className="text-2xl font-bold text-[#1E3064]">Geek<span className="text-[#F97530]">Broadband</span></span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Customer Portal</h1>
          <p className="text-slate-600 mt-1">Sign in to manage your broadband service</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#1E3064] focus:border-[#1E3064] outline-none transition"
              placeholder="your@email.com"
              required
              data-testid="portal-email-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#1E3064] focus:border-[#1E3064] outline-none transition"
              placeholder="Enter password"
              required
              data-testid="portal-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#1E3064] to-[#2a4080] text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="portal-login-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <a href="/" className="text-sm text-slate-500 hover:text-[#1E3064] flex items-center justify-center gap-1">
            ← Back to website
          </a>
        </div>
      </div>
    </div>
  );
};

// ============== DASHBOARD ==============
const Dashboard = ({ token, customer, onLogout }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  const [error, setError] = useState('');

  const loadDashboard = useCallback(async () => {
    try {
      const data = await portalApi.getDashboard(token);
      setDashboardData(data);
    } catch (err) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3064]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="bg-white rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-slate-700">{error}</p>
          <button onClick={onLogout} className="mt-4 text-[#1E3064] hover:underline">
            Sign out and try again
          </button>
        </div>
      </div>
    );
  }

  const { active_order, order_flow_steps } = dashboardData || {};
  const needsAgreement = active_order && 
                          active_order.agreement_sent && 
                          !active_order.agreement_signed;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img 
              src="/images/geekbroadband-logo.webp" 
              alt="GeekBroadband" 
              className="h-10 object-contain"
              onError={(e) => { 
                e.target.style.display = 'none'; 
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-r from-[#F97530] to-[#FF9F5B] rounded-lg flex items-center justify-center">
                <Wifi className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">GeekBroadband</h1>
                <p className="text-xs text-slate-500">Customer Portal</p>
              </div>
            </div>
          </a>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">
              Welcome, {customer.first_name}
            </span>
            <button
              onClick={onLogout}
              className="p-2 text-slate-400 hover:text-red-500 transition"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Alert for pending agreement */}
        {needsAgreement && activeView !== 'agreement' && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <p className="text-amber-800">
                <strong>Action Required:</strong> Please sign your Broadband Service Agreement to proceed.
              </p>
            </div>
            <button
              onClick={() => setActiveView('agreement')}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition flex items-center gap-1"
            >
              Sign Now <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveView('overview')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeView === 'overview'
                ? 'bg-[#1E3064] text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            Overview
          </button>
          {active_order && active_order.agreement_sent && (
            <button
              onClick={() => setActiveView('agreement')}
              className={`px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
                activeView === 'agreement'
                  ? 'bg-[#1E3064] text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              <FileText className="w-4 h-4" />
              Agreement
              {needsAgreement && (
                <span className="w-2 h-2 bg-amber-500 rounded-full" />
              )}
            </button>
          )}
        </div>

        {/* View Content */}
        {activeView === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Order Progress */}
            {active_order && order_flow_steps && (
              <div className="lg:col-span-1">
                <OrderProgress 
                  flowProgress={active_order.flow_progress || {}} 
                  steps={order_flow_steps} 
                />
              </div>
            )}

            {/* Order Details */}
            <div className="lg:col-span-2 space-y-6">
              {active_order ? (
                <>
                  {/* Current Order Card */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-slate-900">Current Order</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        active_order.status === 'active' ? 'bg-green-100 text-green-700' :
                        active_order.status === 'new' ? 'bg-blue-100 text-blue-700' :
                        active_order.status === 'confirmed' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {active_order.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <Package className="w-5 h-5 text-[#1E3064]" />
                        <div>
                          <p className="text-sm text-slate-500">Package</p>
                          <p className="font-medium text-slate-900">{active_order.package_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-5 h-5 text-[#F97530] font-bold">€</span>
                        <div>
                          <p className="text-sm text-slate-500">Monthly</p>
                          <p className="font-medium text-slate-900">€{active_order.total_monthly?.toFixed(2)} <span className="text-xs text-slate-400 font-normal">excl. VAT</span></p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-[#1E3064]" />
                        <div>
                          <p className="text-sm text-slate-500">Installation Address</p>
                          <p className="font-medium text-slate-900">
                            {active_order.installation_address?.eircode}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-[#1E3064]" />
                        <div>
                          <p className="text-sm text-slate-500">Order Number</p>
                          <p className="font-medium text-slate-900">{active_order.order_number}</p>
                        </div>
                      </div>
                    </div>

                    {active_order.scheduled_install_date && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="text-sm text-slate-500">Scheduled Installation</p>
                            <p className="font-medium text-green-700">
                              {new Date(active_order.scheduled_install_date).toLocaleDateString('en-IE', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                              {active_order.scheduled_install_slot && ` - ${active_order.scheduled_install_slot}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Account Info */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Account Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Name</p>
                          <p className="font-medium text-slate-900">{active_order.first_name} {active_order.last_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Email</p>
                          <p className="font-medium text-slate-900">{active_order.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Phone</p>
                          <p className="font-medium text-slate-900">{active_order.phone}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No Active Order</h3>
                  <p className="text-slate-600">
                    You don't have any active orders at the moment.
                  </p>
                  <a 
                    href="/"
                    className="inline-block mt-4 px-6 py-2 bg-[#1E3064] text-white rounded-lg font-medium hover:bg-[#2a4080] transition"
                  >
                    Browse Packages
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {activeView === 'agreement' && active_order && (
          <AgreementSigning 
            order={active_order} 
            token={token}
            onSigned={() => {
              loadDashboard();
              setActiveView('overview');
            }}
          />
        )}
      </main>
    </div>
  );
};

// ============== PASSWORD SETUP SCREEN ==============
const SetupPasswordScreen = ({ setupToken, onComplete }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await portalApi.setupPassword(setupToken, password);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to set password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          {/* GeekBroadband Logo */}
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/images/geekbroadband-logo.webp" 
              alt="GeekBroadband" 
              className="h-10 object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Account Set Up!</h2>
          <p className="text-slate-600 mb-6">Your password has been created. You can now sign in to your Customer Portal.</p>
          <button
            onClick={onComplete}
            className="w-full bg-gradient-to-r from-[#1E3064] to-[#2a4080] text-white py-3 rounded-lg font-semibold hover:shadow-lg transition"
          >
            Sign In Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        {/* GeekBroadband Logo */}
        <div className="flex items-center justify-center mb-8">
          <img 
            src="/images/geekbroadband-logo.webp" 
            alt="GeekBroadband" 
            className="h-12 object-contain"
            onError={(e) => { 
              e.target.style.display = 'none'; 
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          <div className="hidden items-center gap-2">
            <Wifi className="w-8 h-8 text-[#1E3064]" />
            <span className="text-2xl font-bold text-[#1E3064]">Geek<span className="text-[#F97530]">Broadband</span></span>
          </div>
        </div>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-slate-900">Set Up Your Account</h2>
          <p className="text-slate-600 mt-2">Create a password to access your Customer Portal</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#1E3064] focus:border-transparent outline-none"
              placeholder="At least 8 characters"
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#1E3064] focus:border-transparent outline-none"
              placeholder="Confirm your password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#F97530] to-[#FF9F5B] text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <a href="/portal" className="text-sm text-slate-500 hover:text-[#1E3064]">
            Already have an account? Sign in
          </a>
        </div>
      </div>
    </div>
  );
};

// ============== MAIN PORTAL COMPONENT ==============
const CustomerPortal = () => {
  const [token, setToken] = useState(localStorage.getItem('portalToken'));
  const [customer, setCustomer] = useState(
    localStorage.getItem('portalCustomer') 
      ? JSON.parse(localStorage.getItem('portalCustomer')) 
      : null
  );
  
  // Check for setup token in URL
  const [setupToken, setSetupToken] = useState(null);
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    if (tokenParam && window.location.pathname.includes('/setup')) {
      setSetupToken(tokenParam);
    }
  }, []);

  const handleLogin = (newToken, customerData) => {
    setToken(newToken);
    setCustomer(customerData);
    localStorage.setItem('portalToken', newToken);
    localStorage.setItem('portalCustomer', JSON.stringify(customerData));
  };

  const handleLogout = () => {
    setToken(null);
    setCustomer(null);
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalCustomer');
  };
  
  const handleSetupComplete = () => {
    setSetupToken(null);
    // Clear the URL params
    window.history.replaceState({}, document.title, '/portal');
  };
  
  // Show password setup screen if token present
  if (setupToken) {
    return <SetupPasswordScreen setupToken={setupToken} onComplete={handleSetupComplete} />;
  }

  if (!token || !customer) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return <Dashboard token={token} customer={customer} onLogout={handleLogout} />;
};

export default CustomerPortal;
